// src/pages/ProductDetailsPage.js
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useLocation  } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import CartDrawer from "../components/CartDrawer";
import AddedToBagToast from "../components/AddedToBagToast";
import { flyImageToCart } from "../utils/flyToCart";
import ProductCard from "../components/ProductCard";
import { getProductBySlug } from "../services/products.api";
import { FaAlignLeft, FaCubes, FaRulerVertical, FaTruck } from "react-icons/fa";
import { apiRequest } from "../services/api";
import { getAllProducts } from "../services/products.api";
import { mapBackendProduct } from "../utils/mapProduct";
import { PRODUCT_CATEGORIES, PRODUCT_TAGS } from "../data/products";
import { useSeo } from "../hooks/useSeo";

const MediaViewer = React.memo(function MediaViewer({
  media,
  activeIndex,
  setActiveIndex,
  mainImageRef,
  viewerOpen,
  setViewerOpen,
}) {
  const activeMedia = media[activeIndex];
  const isVideo = /\.(mp4|webm|ogg)$/i.test(activeMedia);

  if (!activeMedia) return null;

  return (
    <div className="relative">
      {isVideo ? (
        <video
          ref={mainImageRef}
          src={activeMedia}
          onClick={() => setViewerOpen(true)}
          className="w-full max-h-[520px] object-contain rounded-lg bg-black cursor-zoom-in"
          controls
          playsInline
        />
      ) : (
        <img
          ref={mainImageRef}
          src={activeMedia}
          onClick={() => setViewerOpen(true)}
          className="w-full max-h-[520px] object-contain rounded-lg bg-black cursor-zoom-in"
          decoding="async"
          fetchpriority="high"
        />
      )}
    </div>
  );
});

function ProductDetailsPage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  const [showExtras, setShowExtras] = useState(false);

  const { slug } = useParams();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const [principalCategory, setPrincipalCategory] = useState(null);
const [outfitProducts, setOutfitProducts] = useState([]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [error, setError] = useState("");
  const [isPlacing, setIsPlacing] = useState(false);

  const [relatedProducts, setRelatedProducts] = useState([]);

  const [showWhatsappCTA, setShowWhatsappCTA] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // ✅ Details dropdowns (same behavior as Esiell)
  const [openDesc, setOpenDesc] = useState(false);
  const [openMaterial, setOpenMaterial] = useState(false);
  const [openHeel, setOpenHeel] = useState(false);
  const [openShip, setOpenShip] = useState(false);

  // ✅ Fullscreen viewer
  const [viewerOpen, setViewerOpen] = useState(false);

  const [productTags, setProductTags] = useState([]);


  // ✅ Shipping settings (same logic as Esiell)
  const [shipping, setShipping] = useState({
    fee: 4,
    free_enabled: false,
    free_threshold: 0,
  });

 
const preloadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.src = src;
    img.onload = resolve;
    img.onerror = resolve;
  });


const normalizeColor = (v) =>
  String(v || "")
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[-_]/g, "");

  const mainImageRef = useRef(null);
  
  const allProductsRef = useRef(null);

 useEffect(() => {
  let mounted = true;
  if (!product?.seo_slug) return;
if (!allProductsRef.current) {
  allProductsRef.current = getAllProducts({});
}

allProductsRef.current.then((rows) => {

      if (!mounted) return;

      const found = (rows || []).find(
        (r) => r.seo_slug === product.seo_slug
      );

      if (!found) return;

      setPrincipalCategory(found.primary_category || null);

      // 🔥 FIX: derive tags EXACTLY like product cards
const tags = [];

// explicit tags
if (found.is_new_in) tags.push(PRODUCT_TAGS.NEW_IN);
if (found.is_bestseller) tags.push(PRODUCT_TAGS.BESTSELLER);
if (Number(found.sale_percent) > 0) tags.push(PRODUCT_TAGS.SALE);

// 🔥 FALLBACK: category-based style tag
if (found.primary_category) {
  tags.push(found.primary_category);
}

// 🔥 FINAL SAFETY: never allow empty tags
if (!tags.length) {
  tags.push("default");
}


      setProductTags(tags);
    })
    .catch(() => {});

  return () => {
    mounted = false;
  };
}, [product?.seo_slug]);



 useEffect(() => {
  let mounted = true;
  if (!product || !principalCategory) return;

if (!allProductsRef.current) {
  allProductsRef.current = getAllProducts({});
}

allProductsRef.current.then((rows) => {

      if (!mounted) return;

      const mapped = (rows || [])
        .map(mapBackendProduct)
        .filter((p) => p.id !== product.id);

      const hasTags = productTags.length > 0;

/* ---------- COMPLETE YOUR OUTFIT (FIXED) ---------- */
const scored = mapped
  .filter(
  (p) =>
    String(p.category || "") !== String(principalCategory || "")
)

  .map((p) => {
const mutualTags = (p.tags || []).filter((t) =>
  productTags.includes(t)
);

// 🔥 fallback: same style via category
const fallbackMatch =
  !mutualTags.length &&
  String(p.category || "") !== "" &&
  String(p.category || "") !== String(principalCategory || "");

return {
  ...p,
  __mutualTagCount: mutualTags.length || (fallbackMatch ? 1 : 0),
};


    return {
      ...p,
      __mutualTagCount: mutualTags.length,
    };
  })
  // ✅ must have at least ONE mutual tag
  .filter((p) => p.__mutualTagCount > 0)
  // ✅ sort by most mutual tags first
  .sort((a, b) => b.__mutualTagCount - a.__mutualTagCount);

const usedCategories = new Set();
const outfit = [];

for (const p of scored) {
  if (usedCategories.has(p.category)) continue;

  usedCategories.add(p.category);
  outfit.push(p);

  if (outfit.length === 5) break;
}

setOutfitProducts(outfit);


      /* ---------- RELATED PRODUCTS ---------- */
let related = mapped
  .filter(
    (p) =>
      String(p.category || "") === String(principalCategory || "")
  )
  .map((p) => {
    const shared = (p.tags || []).filter((t) =>
      productTags.includes(t)
    ).length;

    // 🔥 fallback score so category-only products are allowed
    return { ...p, __score: shared || 1 };
  })
  .sort((a, b) => b.__score - a.__score)
  .slice(0, 5);


      setRelatedProducts(related);
    })
    .catch(() => {});

  return () => {
    mounted = false;
  };
}, [product?.id, principalCategory, productTags]);

// ✅ media gallery (images/videos) like Esiell
const media = useMemo(() => product?.images?.map((i) => i.url) || [], [product]);


useEffect(() => {
  if (!media.length) return;

  const next = media[activeIndex + 1];
  const prev = media[activeIndex - 1];

  if (next) preloadImage(next);
  if (prev) preloadImage(prev);
}, [activeIndex, media]);



  useEffect(() => {
    const t = setTimeout(() => setShowWhatsappCTA(true), 1000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    apiRequest("/shipping-settings")
      .then((s) =>
        setShipping({
          fee: Number(s.default_fee ?? 4),
          free_enabled: !!s.free_enabled,
          free_threshold: Number(s.free_threshold ?? 0),
        })
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setProduct(null);

    setActiveIndex(0);
    setSelectedColor(null);
    setSelectedVariant(null);
    setError("");

    getProductBySlug(slug)
      .then((data) => {
        if (!mounted) return;
        setProduct(data);

        // ✅ auto-select first available color (same UX as Esiell: color preselected, size not)
       const first = (data.variants || []).find((v) => v.is_available);
if (first) setSelectedColor(first.color_name);
      })
      .finally(() => setLoading(false));

    return () => (mounted = false);
  }, [slug]);

const colorToImageIndex = useMemo(() => {
  if (!product?.images) return {};

  const map = {};

  product.images.forEach((img, index) => {
    if (!img.color_name) return;

    const key = normalizeColor(img.color_name);

    if (
      map[key] === undefined ||
      Number(img.sort_order) <
        Number(product.images[map[key]]?.sort_order ?? Infinity)
    ) {
      map[key] = index;
    }
  });

  return map;
}, [product?.id]);

const scrollToColorImage = async (colorName) => {
  const key = normalizeColor(colorName);
  const index = colorToImageIndex[key];

  if (typeof index === "number") {
    const src = media[index];
    if (src) await preloadImage(src); // 🚀 decode first
    if (activeIndex !== index) {
  setActiveIndex(index);
}

  }
};


  const displayName = product
    ? lang === "EN"
      ? product.name_en
      : product.name_ar
    : "";

  const description = product
    ? lang === "EN"
      ? product.description_en || product.short_desc_en || ""
      : product.description_ar || product.short_desc_ar || ""
    : "";

  const material = product
    ? lang === "EN"
      ? product.material_en
      : product.material_ar
    : "";

  const heel = product?.heel_height;

  const discount = Number(product?.sale_percent) || 0;
  const hasDiscount = discount > 0;
  const finalPrice = product
    ? hasDiscount
      ? Math.round(product.base_price * (1 - discount / 100))
      : product.base_price
    : 0;

  const availableVariants = useMemo(
  () => (product?.variants || []).filter((v) => v.is_available),
  [product]
);

const isOutOfStock = availableVariants.length === 0;

  const colors = useMemo(() => {
    const map = new Map();
    availableVariants.forEach((v) => {
      if (!map.has(v.color_name)) map.set(v.color_name, v.color_hex);
    });
    return Array.from(map.entries()).map(([name, code]) => ({ name, code }));
  }, [availableVariants]);

  const sizesByColor = useMemo(() => {
    if (!selectedColor) return [];
    return availableVariants.filter((v) => v.color_name === selectedColor);
  }, [availableVariants, selectedColor]);


  const activeMedia = media[activeIndex];
  const isVideo = /\.(mp4|webm|ogg)$/i.test(activeMedia);

const touchStartX = useRef(0);
const touchEndX = useRef(0);

const handleTouchStart = (e) => {
  touchStartX.current = e.changedTouches?.[0]?.clientX || e.clientX;
};

const handleTouchEnd = (e) => {
  touchEndX.current = e.changedTouches?.[0]?.clientX || e.clientX;

  const diff = touchStartX.current - touchEndX.current;

  if (Math.abs(diff) < 50) return;

  if (diff > 0) {
    nextMedia();
  } else {
    prevMedia();
  }
};


  const prevMedia = () => {
    if (!media.length) return;
    setActiveIndex((i) => (i === 0 ? media.length - 1 : i - 1));
  };

  const nextMedia = () => {
    if (!media.length) return;
    setActiveIndex((i) => (i === media.length - 1 ? 0 : i + 1));
  };

  

// ✅ WhatsApp CTA (Lions)
const whatsappMessage = `Hi, I need help with: ${displayName} (${product?.public_id || ""})`;
const whatsappLink = `https://wa.me/96170203205?text=${encodeURIComponent(
  whatsappMessage
)}`;

useEffect(() => {
  if (!product?.id) return;

  // Your internal analytics
  apiRequest("/analytics/track", {
    method: "POST",
    body: JSON.stringify({
      action_type: "view_product",
      entity_type: "product",
      entity_id: product.id,
    }),
  }).catch(() => {});

  // 🔥 META VIEW CONTENT
  if (window.fbq) {
    window.fbq("track", "ViewContent", {
      content_ids: [product.public_id || product.id],
      content_type: "product",
      value: Number(finalPrice) || 0,
      currency: "USD",
    });
  }
}, [product?.id]);



useEffect(() => {
  if (!product || !media.length) return;

  const params = new URLSearchParams(location.search);
  const imgIndex = Number(params.get("img"));

  if (
    Number.isInteger(imgIndex) &&
    imgIndex >= 0 &&
    imgIndex < media.length
  ) {
    setActiveIndex(imgIndex);
  }
}, [product?.id, media.length]);

useEffect(() => {
  if (!product || !media.length) return;

  const params = new URLSearchParams(location.search);
  const colorParam = params.get("color");

  if (!colorParam) return;

  const key = normalizeColor(colorParam);
  const index = colorToImageIndex[key];

  if (typeof index === "number") {
    setSelectedColor(colorParam);
    setActiveIndex(index);
  }
}, [product?.id, media.length, colorToImageIndex]);

const TEXT = useMemo(
  () => ({
    EN: {
      addToBag: "ADD TO CART",
      selectVariant: "Please select color and size.",
      color: "Color",
      size: "Size",
      description: "Description",
      material: "Material",
      heelHeight: "Heel Height",
      shipping: "Shipping & Delivery",
      pickupInfo: "Pick up from store",
      deliveryInfo: "Delivery fees",
      freeDeliveryInfo: "Free delivery on orders above",
      cm: "cm",
    },
    AR: {
      addToBag: "أضيفي إلى السلة",
      selectVariant: "يرجى اختيار اللون والمقاس.",
      color: "اللون",
      size: "المقاس",
      description: "الوصف",
      material: "الخامة",
      heelHeight: "ارتفاع الكعب",
      shipping: "التوصيل والاستلام",
      pickupInfo: "استلام من المحل",
      deliveryInfo: "رسوم التوصيل",
      freeDeliveryInfo: "توصيل مجاني للطلبات فوق",
      cm: "سم",
    },
  }[lang]),
  [lang]
);


  const handleAddToBag = () => {
    if (!selectedColor || !selectedVariant) {
      setError(TEXT.selectVariant);
      return;
    }

    if (!selectedVariant?.is_available) {
  setError(lang === "EN" ? "This variant is not available." : "هذا الخيار غير متوفر.");
  return;
}

    setError("");
    setIsPlacing(true);

    if (mainImageRef.current) {
      flyImageToCart(mainImageRef.current);
    }

    apiRequest("/analytics/track", {
  method: "POST",
  body: JSON.stringify({
    action_type: "add_to_cart",
    entity_type: "product",
    entity_id: product.id,
  }),
}).catch(() => {});

// 🔥 META ADD TO CART
if (window.fbq) {
  window.fbq("track", "AddToCart", {
    content_ids: [product.public_id || product.id],
    content_type: "product",
    value: Number(finalPrice) || 0,
    currency: "USD",
  });
}



    addToCart({
      product_id: product.id,
      variant_id: selectedVariant.id,
      public_id: product.public_id,
      nameEN: product.name_en,
      nameAR: product.name_ar,
      price: finalPrice + (selectedVariant.extra_price || 0),
      image: product.images?.[0]?.url,
      color: selectedColor,
      size: selectedVariant.size,
    });

    setTimeout(() => setIsPlacing(false), 800);
  };

useEffect(() => {
  const t = setTimeout(() => setShowExtras(true), 300);
  return () => clearTimeout(t);
}, []);

useSeo(
  "product",
  product?.id,
  product
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: displayName,
        description: description,
        sku: product.public_id || product.id,
        image: product.images?.map((i) => i.url),
        brand: {
          "@type": "Brand",
          name: "Lions",
        },
        offers: {
          "@type": "Offer",
          priceCurrency: "USD",
          price: finalPrice,
          availability:
  (product?.variants || []).some((v) => v.is_available)
    ? "https://schema.org/InStock"
    : "https://schema.org/OutOfStock",
          url: typeof window !== "undefined" ? window.location.href : "",
        },
      }
    : null
);

  if (loading) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center bg-[#0F1A12] text-white">
        Loading product…
      </div>
    );
  }

  if (!product) {
    return (
      <div className="w-full min-h-screen bg-[#0F1A12] text-white pt-[95px] px-4">
        <button
          onClick={() => navigate(-1)}
          className="absolute top-[110px] left-4 text-3xl opacity-60"
        >
          ×
        </button>
        <p className="mt-20 text-center">Product not found.</p>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#0F1A12] text-white overflow-x-hidden">
      <TopBanner />
      <MainHeader />
      <CartDrawer />
      <AddedToBagToast />

      <div className="pt-[115px] px-4 pb-24 max-w-5xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="absolute top-[110px] left-4 text-3xl opacity-60"
        >
          ×
        </button>

        <div className="grid md:grid-cols-2 gap-6">
          {/* MEDIA (same structure/behavior as Esiell) */}
          <div>
<div
  className="relative aspect-square bg-black rounded-lg overflow-hidden cursor-grab active:cursor-grabbing"
  onTouchStart={handleTouchStart}
  onTouchEnd={handleTouchEnd}
  onMouseDown={handleTouchStart}
  onMouseUp={handleTouchEnd}
>

  <MediaViewer
    media={media}
    activeIndex={activeIndex}
    setActiveIndex={setActiveIndex}
    mainImageRef={mainImageRef}
    viewerOpen={viewerOpen}
    setViewerOpen={setViewerOpen}
  />

  {/* PREV / NEXT */}
  {media.length > 1 && (
    <>
      <button
        onClick={prevMedia}
        className="
          absolute left-0 top-1/2 -translate-y-1/2
          w-8 h-8
          bg-white/15 hover:bg-white/10
          text-white text-sm font-bold
          flex items-center justify-center
          rounded
        "
      >
        ‹
      </button>

      <button
        onClick={nextMedia}
        className="
          absolute right-0 top-1/2 -translate-y-1/2
          w-8 h-8
          bg-white/15 hover:bg-white/10
          text-white text-sm font-bold
          flex items-center justify-center
          rounded
        "
      >
        ›
      </button>
    </>
  )}

  {/* DOTS */}
  {media.length > 1 && (
    <div className="absolute bottom-3 right-3 flex gap-2">
      {media.map((_, i) => (
        <button
          key={i}
          onClick={() => {
  if (i !== activeIndex) setActiveIndex(i);
}}

          className={`
            w-2.5 h-2.5 rounded-full border
            ${
              i === activeIndex
                ? "bg-white border-white"
                : "border-white/70"
            }
          `}
        />
      ))}
    </div>
  )}
</div>

            </div>
          </div>

          {/* INFO */}
          <div>
            <h1 className="text-xl font-semibold">{displayName}</h1>

            {product.public_id && (
              <p className="text-xs text-white/50 mt-1">{product.public_id}</p>
            )}

            <div className="mt-3 flex items-center gap-2">
              <span className="text-lg font-bold">${finalPrice}</span>
              {hasDiscount && (
                <span className="text-sm line-through text-white/40">
                  ${product.base_price}
                </span>
              )}
            </div>

            {/* COLOR */}
            <div className="mt-5">
              <p className="text-xs font-semibold mb-2">{TEXT.color}</p>
              <div className="flex gap-2">
                {colors.map((c) => (
                  <button
                    key={c.name}
                    onClick={() => {
  setSelectedColor(c.name);
  setSelectedVariant(null);
  setError("");
  scrollToColorImage(c.name);
}}

                    className={`w-8 h-8 rounded-full border ${
                      selectedColor === c.name
                        ? "border-white ring-2 ring-white/30"
                        : "border-white/30"
                    }`}
                    style={{ backgroundColor: c.code }}
                    aria-label={`Color ${c.name}`}
                    title={c.name}
                  />
                ))}
              </div>
            </div>

            {/* SIZE */}
            {sizesByColor.length > 0 && (
              <div className="mt-5">
                <p className="text-xs font-semibold mb-2">{TEXT.size}</p>
                <div className="flex flex-wrap gap-2">
                  {sizesByColor.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => {
                        setSelectedVariant(v);
                        setError("");
                      }}
                      className={`px-3 py-2 text-xs rounded-full border ${
                        selectedVariant?.id === v.id
                          ? "bg-white text-black border-white"
                          : "border-white/30"
                      }`}
                    >
                      {v.size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
<button
  onClick={handleAddToBag}
  disabled={isPlacing || isOutOfStock}
  className={`mt-6 w-full py-3 rounded-md font-semibold transition ${
    isOutOfStock
      ? "bg-white/40 text-black/50 cursor-not-allowed"
      : "bg-white text-black"
  }`}
>
  {isOutOfStock
    ? lang === "EN"
      ? "OUT OF STOCK"
      : "غير متوفر"
    : TEXT.addToBag}
</button>


            {/* DETAILS DROPDOWNS (same UX as Esiell, dark-styled) */}
            <div className="mt-6 border border-white/15 rounded-lg overflow-hidden">
              {/* 1) Description */}
              <div
                className="px-4 py-3 border-b border-white/10 flex justify-between items-center cursor-pointer"
                onClick={() => setOpenDesc((v) => !v)}
              >
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <FaAlignLeft className="text-white/60" />
                  <span>{TEXT.description}</span>
                </div>
                <span className="text-xl">{openDesc ? "−" : "+"}</span>
              </div>

              <div
  className={`
    px-4 text-xs leading-5 text-white/75 transition-all duration-300
    ${openDesc ? "max-h-40 opacity-100 py-3" : "max-h-0 opacity-0 py-0"}
    overflow-y-auto
    scrollbar-thin scrollbar-thumb-white/30 scrollbar-track-transparent
  `}
>
  <div className="pr-2">
    {description || "—"}
  </div>
</div>


              {/* 2) Material */}
              <div
                className="px-4 py-3 border-t border-white/10 flex justify-between items-center cursor-pointer"
                onClick={() => setOpenMaterial((v) => !v)}
              >
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <FaCubes className="text-white/60" />
                  <span>{TEXT.material}</span>
                </div>
                <span className="text-xl">{openMaterial ? "−" : "+"}</span>
              </div>

              <div
                className={`
                  px-4 text-xs leading-5 text-white/75 transition-all duration-300
                  ${openMaterial ? "max-h-24 opacity-100 py-3" : "max-h-0 opacity-0 py-0"}
                  overflow-hidden
                `}
              >
                <p>{material || "—"}</p>
              </div>

              {/* 3) Heel Height (ONLY if > 0) */}
              {Number(heel) > 0 && (
                <>
                  <div
                    className="px-4 py-3 border-t border-white/10 flex justify-between items-center cursor-pointer"
                    onClick={() => setOpenHeel((v) => !v)}
                  >
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <FaRulerVertical className="text-white/60" />
                      <span>{TEXT.heelHeight}</span>
                    </div>
                    <span className="text-xl">{openHeel ? "−" : "+"}</span>
                  </div>

                  <div
                    className={`
                      px-4 text-xs leading-5 text-white/75 transition-all duration-300
                      ${openHeel ? "max-h-24 opacity-100 py-3" : "max-h-0 opacity-0 py-0"}
                      overflow-hidden
                    `}
                  >
                    <p>
                      {Number(heel)} {TEXT.cm}
                    </p>
                  </div>
                </>
              )}

              {/* 4) Shipping & Delivery */}
              <div
                className="px-4 py-3 border-t border-white/10 flex justify-between items-center cursor-pointer"
                onClick={() => setOpenShip((v) => !v)}
              >
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <FaTruck className="text-white/60" />
                  <span>{TEXT.shipping}</span>
                </div>
                <span className="text-xl">{openShip ? "−" : "+"}</span>
              </div>

              <div
                className={`
                  px-4 text-xs leading-5 text-white/75 transition-all duration-300
                  ${openShip ? "max-h-40 opacity-100 py-3" : "max-h-0 opacity-0 py-0"}
                  overflow-hidden
                `}
              >
                <p>• {TEXT.deliveryInfo}: ${Number(shipping.fee || 0).toFixed(2)}</p>

                {shipping.free_enabled && Number(shipping.free_threshold) > 0 && (
                  <p className="mt-1">
                    • {TEXT.freeDeliveryInfo}: ${Number(shipping.free_threshold).toFixed(2)}
                  </p>
                )}

                <p className="mt-1">• {TEXT.pickupInfo}: $0.00</p>
              </div>
            </div>
          </div>
        </div>

        {/* COMPLETE YOUR OUTFIT */}
{showExtras && outfitProducts.length > 0 && (
  <div className="mt-12">
<h2 className="text-lg font-semibold mb-6 px-1">
  {lang === "EN" ? "Complete the Loadout" : "أكمل تجهيزك"}
</h2>

    <div className="flex gap-3 overflow-x-auto px-1 pb-2">
      {outfitProducts.map((p) => (
        <div key={p.id} className="min-w-[220px]">
          <ProductCard product={p} />
        </div>
      ))}
    </div>
  </div>
)}


       {/* RELATED PRODUCTS */}
{relatedProducts.length > 0 && (
  <div className="mt-14">
    <h2 className="text-lg font-semibold mb-5 px-1">
      {lang === "EN" ? "Related products" : "منتجات مشابهة"}
    </h2>

    <div className="flex gap-4 overflow-x-auto px-1 pb-3 snap-x snap-mandatory">
      {relatedProducts.map((p) => (
        <div
          key={p.id}
          className="min-w-[220px] snap-start"
        >
          <ProductCard product={p} />
        </div>
      ))}
    </div>
  </div>
)}

      

{/* ✅ STICKY WHATSAPP CTA (Lions – same as Esiell) */}
{showWhatsappCTA && (
  <div
    className="fixed z-[9999] bottom-20 right-5 flex flex-col items-center gap-2"
    dir="ltr"
  >
    {/* Close button */}
    <button
      onClick={() => setShowWhatsappCTA(false)}
      className="
        w-7 h-7 rounded-full
        bg-white border border-white/30
        flex items-center justify-center
        text-black text-sm
        shadow-md
        opacity-90 hover:opacity-100
      "
      aria-label="Close WhatsApp"
      title="Close"
    >
      ×
    </button>

    {/* WhatsApp Button */}
    <a
      href={whatsappLink}
      target="_blank"
      rel="noreferrer"
      className="
        w-14 h-14 rounded-full
        flex items-center justify-center
        shadow-lg
        active:scale-95
        transition
      "
      style={{ backgroundColor: "#25D366" }}
      aria-label="WhatsApp"
      title="WhatsApp"
    >
      <img
        src="/icons/whatsapp.png"
        alt="WhatsApp"
        className="w-7 h-7 object-contain"
      />
    </a>
  </div>
)}

      {/* SCROLL TO TOP (kept) */}
      {showScrollTop && (
  <button
    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    className="
      fixed top-24 left-1/2
      -translate-x-1/2
      w-12 h-12
      bg-white text-black
      rounded-full
      shadow-lg
    "
    aria-label="Scroll to top"
    title="Scroll to top"
  >
    ↑
  </button>
)}


      {/* FULLSCREEN VIEWER (same behavior as Esiell) */}
      {viewerOpen && (
  <div className="fixed inset-0 z-[10000] bg-black/95 flex items-center justify-center">

    {/* BACKDROP LAYER */}
    <div
      className="absolute inset-0"
      onClick={() => setViewerOpen(false)}
    />

    {/* CLOSE BUTTON */}
    <button
      onClick={() => setViewerOpen(false)}
      className="absolute top-4 right-4 text-white text-3xl opacity-80 hover:opacity-100 z-10"
      aria-label="Close viewer"
      title="Close"
    >
      ×
    </button>

    {/* CONTENT */}
    <div className="relative z-10 w-full h-full flex overflow-x-auto snap-x snap-mandatory scroll-smooth">
      {media.map((m, i) => {
        const vid = /\.(mp4|webm|ogg)$/i.test(m);
        return (
          <div
            key={i}
            className="min-w-full h-full flex items-center justify-center snap-center"
          >
            {vid ? (
              <video
                src={m}
                controls
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <img
                src={m}
                alt=""
                className="max-w-full max-h-full object-contain"
              />
            )}
          </div>
        );
      })}
    </div>
  </div>
)}

    </div>
  );
}

export default ProductDetailsPage;
